import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { AuthGuard, UnauthGuard } from './shared/guards';
import { DefaultComponent } from './layouts/default/default.component';
import { PortalComponent } from './layouts/portal/portal.component';
import { LoginComponent } from './modules/login/login.component';
import { Page404Component } from './modules/page404/page404.component';
import { Page403Component } from './modules/page403/page403.component';
import { HomeComponent } from './modules/home/home.component';
import { SignUpComponent } from './modules/sign-up/sign-up.component';
import { SiteDetailsMapComponent } from './modules/projects/components/maps/site-details-map/site-details-map.component';
import { DailyReportMapContainerComponent } from './modules/projects/components/maps/daily-report-map-container/daily-report-map-container.component';
import { ProjectsWrapperComponent } from './modules/projects/wrapper/projects-wrapper.component';
import { ProjectsLocationMapComponent } from './modules/projects/components/maps/projects-location-map/projects-location-map.component';
import { ProjectsPageComponent } from './modules/projects/pages/projects-page/screen/projects-page.component';
import { SitesLocationMapComponent } from './modules/projects/components/maps/sites-location-map/sites-location-map.component';
import { MapLayerContainerComponent } from './modules/projects/components/maps/map-layer-container/map-layer-container.component';
import { ProjectDetailsScreenComponent } from './modules/projects/pages/projects-details-page/screen/project-details-screen.component';
import { SiteDetailsScreenComponent } from './modules/projects/pages/site-details-page/screen/site-details-screen.component';
import { MappedReportViewerPageComponent } from './modules/projects/pages/mapped-report-viewer-page/screen/mapped-report-viewer-page.component';
import { SearchWrapperComponent } from './modules/search/search-wrapper/search-wrapper.component';
import { SearchDetailsComponent } from './modules/search/components/search-details/search-details.component';
import { ProjectLocationMapComponent } from './modules/search/components/maps/project-location-map/project-location-map.component';

const routes: Routes = [
  {
    path: '',
    component: PortalComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', redirectTo: 'projects', pathMatch: 'full' },
      { path: 'home', component: HomeComponent, canActivate: [AuthGuard] },
      {
        path: 'project',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/project/project.module').then(m => m.ProjectModule),
      },
      {
        path: 'organization',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/organization/organization.module').then(m => m.OrganizationModule),
      },
      {
        path: 'account',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/account/account.module').then(m => m.AccountModule),
      },
      {
        path: 'issues',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/issue/issue.module').then(m => m.IssueModule),
      },
      {
        path: 'offsite-tests',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/offsite-tests/offsite-tests.module').then(m => m.OffsiteTestsModule),
      },
      {
        path: 'reports',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/report/report.module').then(m => m.ReportModule),
      },
      {
        path: 'approvals',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/approvals/approvals.module').then(m => m.ApprovalsModule),
      },
      {
        path: 'submittals',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/submittals/submittals.module').then(m => m.SubmittalsModule),
      },
      {
        path: 'app-settings',
        canActivate: [AuthGuard],
        loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule),
      },
      {
        path: 'projects',
        canActivate: [AuthGuard],
        component: ProjectsWrapperComponent,
        children: [
          {
            path: '',
            children: [
              {
                path: '',
                component: ProjectsLocationMapComponent,
                outlet: 'map',
              },
              {
                path: '',
                component: ProjectsPageComponent,
                outlet: 'content',
              },
            ],
          },
          {
            path: ':projectId',
            children: [
              {
                path: '',
                component: MapLayerContainerComponent,
                outlet: 'map',
              },
              {
                path: '',
                component: ProjectDetailsScreenComponent,
                outlet: 'content',
              },
            ],
          },
          {
            path: ':projectId/sites/:siteId',
            children: [
              {
                path: '',
                component: DailyReportMapContainerComponent,
                outlet: 'map',
              },
              {
                path: '',
                component: SiteDetailsScreenComponent,
                outlet: 'content',
              },
            ],
          },
          {
            path: ':projectId/forms/:dailyReportId/:groupKey',
            children: [
              {
                path: '',
                component: MappedReportViewerPageComponent,
                outlet: 'content',
              },
            ],
          },
          {
            path: '**',
            children: [
              {
                path: '',
                component: Page404Component,
                outlet: 'content',
              },
            ],
          },
        ],
      },
      {
        path: 'search',
        canActivate: [AuthGuard],
        component: SearchWrapperComponent,
        children: [
          {
            path: '',
            children: [
              {
                path: '',
                component: ProjectLocationMapComponent,
                outlet: 'map',
              },
              {
                path: '',
                component: SearchDetailsComponent,
                outlet: 'searchContent',
              },
            ],
          },
          {
            path: '**',
            component: Page404Component,
            outlet: 'searchContent',
          },
        ],
      },
    ],
  },
  {
    path: '',
    component: DefaultComponent,
    children: [
      { path: 'login', component: LoginComponent, canActivate: [UnauthGuard] },
      { path: 'invite/accept/:code', component: SignUpComponent },
      {
        path: 'ga',
        loadChildren: () => import('./modules/guest-actions/guest-actions.module').then(m => m.GuestActionsModule),
      },
      {
        path: 'samples',
        loadChildren: () => import('./modules/samples/samples.module').then(m => m.SamplesModule),
      },
      { path: 'page-403', component: Page403Component },
      { path: '**', component: Page404Component },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {
}
